import React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@/lib/utils';

export interface BudgetGoalSliderProps {
  /** The goal in hundredths of a percent (4000 is 40%) — the unit the goals code compares and sums in. */
  value: number;
  onValueChange: (hundredths: number) => void;
  /** The thumb's accessible name: the slider sits under its group's heading and has no label of its own. */
  label: string;
  /** What a screen reader says for the value ("40%"), in place of the bare number. */
  valueText: string;
  className?: string;
}

// Not ui/slider: its Range fills with bg-primary, and six amber tracks would break the Amber Signal Rule. It is
// the same primitive, so keyboard and touch behave as usual (arrows step 1, Shift+arrows and Page keys step 10,
// Home/End jump to the ends); only the track is neutral, in BudgetBar's tokens. Steps are whole percents (§10.2):
// a goal saved elsewhere as 33.33 still draws at 33.33 — the primitive doesn't snap a controlled value — and the
// first arrow press or drag moves it to a whole number.
//
// With a mouse or pen the whole 32px band is the hit area: pressing anywhere on it moves the thumb there. On touch
// only the thumb starts a drag (its hit area grown to 44px), and the band lets a vertical swipe scroll the page —
// six stacked tracks would otherwise turn every scroll that lands on one into a changed goal.
export const BudgetGoalSlider: React.FC<BudgetGoalSliderProps> = ({ value, onValueChange, label, valueText, className }) => (
  <SliderPrimitive.Root
    value={[value / 100]}
    onValueChange={([percent]) => onValueChange(Math.round(percent * 100))}
    min={0}
    max={100}
    step={1}
    // preventDefault here skips Radix's own handler (composeEventHandlers checks defaultPrevented).
    onPointerDown={(event) => {
      if (event.pointerType === 'touch' && !(event.target as HTMLElement).closest('[role="slider"]')) {
        event.preventDefault();
      }
    }}
    className={cn('relative flex h-8 w-full touch-pan-y select-none items-center', className)}
  >
    <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted">
      <SliderPrimitive.Range className="absolute h-full rounded-full bg-foreground/60" />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb
      aria-label={label}
      aria-valuetext={valueText}
      className="relative block h-5 w-5 touch-none rounded-full after:absolute after:-inset-3 after:content-[''] after:rounded-full border-2 border-foreground/60 bg-background ring-offset-background transition-colors hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    />
  </SliderPrimitive.Root>
);
